import {
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { Overview } from 'components/Overview'
import { Fragment, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { createGoDocsRecord, deleteGoDocsRecord, getGoDocsRecords } from 'services'
import { Button, Modal } from 'stories/components'
import { confirm, formatTime } from 'utils'

export enum GoDocsStatus {
  PENDING = 'Pending',
  COMPLETED = 'Completed',
  FAILED = 'Failed',
}

const openGoDocsUrl = (url: string | null | undefined, username?: string) => {
  if (!url) return
  const resolved = url.replace('[[username]]', encodeURIComponent(username || ''))
  window.open(resolved, '_blank', 'noopener,noreferrer')
}

export default function GoDocsPage({ hasOverview = true }: { hasOverview?: boolean }) {
  const email = useSelector((state: any) => state.auth?.email || state.auth?.profile?.email || '')
  const [loading, setLoading] = useState(false)
  const [records, setRecords] = useState<Array<Record<string, any>>>([])
  const [tableData, setTableData] = useState<Record<string, any>>({})
  const [overrideValues, setOverrideValues] = useState<Record<string, string>>({})
  const [editingItems, setEditingItems] = useState<Set<string>>(new Set())
  const [detail, setDetail] = useState<Record<string, any> | null>(null)

  const init = async () => {
    setLoading(true)
    try {
      const listRes = await getGoDocsRecords().catch(() => null)
      setRecords(listRes?.records || [])
      setTableData(listRes?.tableData || {})
      if (listRes?.overrideValues && Object.keys(listRes.overrideValues).length) {
        setOverrideValues(listRes.overrideValues)
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    init()
  }, [])

  const onImport = async () => {
    setLoading(true)
    try {
      const res = await createGoDocsRecord(overrideValues)
      if (res?.success && res.data) {
        setRecords((prev) => [res.data, ...prev])
        toast('Successfully imported loan to GoDocs.', { type: 'success' })
      } else if (res?.data) {
        setRecords((prev) => [res.data, ...prev])
        toast(res?.message || 'GoDocs import finished with issues.', { type: 'warning' })
      } else {
        toast(res?.message || 'Failed to import to GoDocs.', { type: 'error' })
      }
    } catch (e: any) {
      toast(e?.response?.data?.message || e?.message || 'Failed to import to GoDocs.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const onDelete = async (id: number) => {
    const ok = await confirm('Remove this GoDocs import record from history?')
    if (!ok) return
    setLoading(true)
    try {
      await deleteGoDocsRecord(id)
      setRecords((prev) => prev.filter((r) => r.id !== id))
      toast('Record removed.', { type: 'info' })
    } finally {
      setLoading(false)
    }
  }

  const handleOverrideChange = (keyPath: string, value: string) => {
    setOverrideValues({ ...overrideValues, [keyPath]: value })
  }

  const getOverrideValue = (keyPath: string) => overrideValues[keyPath] || ''

  const clearOverrides = () => {
    setOverrideValues({})
    setEditingItems(new Set())
    toast('Override values cleared', { type: 'info' })
  }

  const toggleEdit = (keyPath: string) => {
    const next = new Set(editingItems)
    if (next.has(keyPath)) next.delete(keyPath)
    else next.add(keyPath)
    setEditingItems(next)
  }

  const isEditing = (keyPath: string) => editingItems.has(keyPath)

  let globalIdx = 1
  const renderTable = () => (
    <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6 mb-2 rounded-md">
      <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200">
        <tr>
          <th scope="col" className="px-3 py-3">
            Category
          </th>
          <th scope="col" className="px-3 py-3 min-w-[200px]">
            Field
          </th>
          <th scope="col" className="px-3 py-3">
            Value
          </th>
          <th scope="col" className="px-3 py-3 min-w-[200px]">
            Override Value
          </th>
          <th scope="col" className="px-3 py-3">
            Status
          </th>
          <th scope="col" className="px-3 py-3">
            Actions
          </th>
        </tr>
      </thead>
      <tbody className="text-[14px] text-gray-900">
        {Object.keys(tableData)
          .sort()
          .map((category, index) => {
            const item = tableData[category]
            const count = Object.keys(item).length
            return (
              <Fragment key={category}>
                {Object.keys(item).map((key, subIndex) => {
                  const fieldData = item[key]
                  const keyPath = fieldData.keyPath || ''
                  const overrideValue = getOverrideValue(keyPath)
                  const hasOverride = overrideValue !== ''
                  return (
                    <tr key={`${category}-${key}`} className={`border ${index % 2 ? 'bg-gray-50' : ''}`}>
                      {!subIndex && (
                        <td className="px-3 py-2" rowSpan={count}>
                          {category}
                        </td>
                      )}
                      <td className="px-3 py-2">
                        {globalIdx++}. {key}
                      </td>
                      <td className="px-3 py-2">{fieldData.value}</td>
                      <td className="px-3 py-2">
                        {isEditing(keyPath) ? (
                          <input
                            type="text"
                            className="w-full px-2 py-1 border border-gray-300 rounded"
                            value={overrideValue}
                            onChange={(e) => handleOverrideChange(keyPath, e.target.value)}
                            autoFocus
                          />
                        ) : (
                          <span className="text-gray-500">{overrideValue || '-'}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {hasOverride || fieldData.status ? (
                          <span className="inline-flex text-shade-blue">
                            <CheckIcon className="w-4 h-4" />
                          </span>
                        ) : (
                          <span className="inline-flex text-red-800">
                            <XMarkIcon className="w-4 h-4" />
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className="cursor-pointer hover-shadow1 p-1 text-shade-blue inline-flex"
                          onClick={() => toggleEdit(keyPath)}
                          title={isEditing(keyPath) ? 'Close' : 'Edit'}
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </Fragment>
            )
          })}
      </tbody>
    </table>
  )

  return (
    <div className="GoDocs-container relative">
      {hasOverview && <Overview title="GoDocs" />}
      <LayoutLoading show={loading} />
      <div className={`relative bg-white rounded mb-4 ${hasOverview ? 'shadow1 md:p-6 p-2' : ''}`}>
        <h2 className="text-2xl font-bold mb-2">GoDocs</h2>
        <p className="text-sm text-gray-600 mb-4">
          Review mapped loan fields, optionally override values, then import into GoDocs. After a successful import, open
          the loan in GoDocs to finish closing documents.
        </p>

        {renderTable()}

        <div className="text-right flex justify-between items-center mt-2">
          <div>
            {Object.keys(overrideValues).length > 0 && (
              <Button color="red" onClick={clearOverrides}>
                {`Clear Overrides (${Object.keys(overrideValues).filter((k) => overrideValues[k]).length})`}
              </Button>
            )}
          </div>
          <div>
            <Button onClick={onImport}>Import to GoDocs</Button>
          </div>
        </div>

        <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6 mt-6">
          <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-3 py-3">
                No
              </th>
              <th scope="col" className="px-3 py-3">
                Reference
              </th>
              <th scope="col" className="px-3 py-3">
                Created By
              </th>
              <th scope="col" className="px-3 py-3">
                Created At
              </th>
              <th scope="col" className="px-3 py-3">
                Status
              </th>
              <th scope="col" className="px-3 py-3">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="text-[14px] text-gray-900">
            {records.map((item, index: number) => (
              <tr key={item.id}>
                <td className="px-3 py-3">{index + 1}</td>
                <td className="px-3 py-3">{item.referenceId || item.recordId || '-'}</td>
                <td className="px-3 py-3">{item.email}</td>
                <td className="px-3 py-3">{formatTime(item.createdAt)}</td>
                <td className="px-3 py-3">
                  <span
                    className={`p-1 min-w-[100px] inline-block text-center rounded-md ${
                      item.status === GoDocsStatus.COMPLETED
                        ? 'bg-green-300'
                        : item.status === GoDocsStatus.FAILED
                        ? 'bg-red-200'
                        : 'bg-gray-300'
                    }`}
                  >
                    {item.status}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <div className="flex gap-2 items-center">
                    {item.redirectUrl && (
                      <span
                        className="p-1 hover-shadow1 cursor-pointer text-shade-blue"
                        title="Open in GoDocs"
                        onClick={() => openGoDocsUrl(item.redirectUrl, email)}
                      >
                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                      </span>
                    )}
                    {item.summaryPageUrl && (
                      <span
                        className="text-xs text-shade-blue cursor-pointer hover:underline"
                        onClick={() => openGoDocsUrl(item.summaryPageUrl, email)}
                      >
                        Summary
                      </span>
                    )}
                    <span
                      className="text-shade-blue p-1 hover-shadow1 cursor-pointer text-xs"
                      onClick={() => setDetail(item)}
                    >
                      Detail
                    </span>
                    <span
                      className="text-red-800 p-1 hover-shadow1 cursor-pointer"
                      onClick={() => onDelete(item.id)}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <Modal title="GoDocs Import Details" onClose={() => setDetail(null)} isOpen>
          <pre className="text-xs overflow-auto max-h-[70vh] bg-slate-50 p-3 rounded">
            {JSON.stringify(
              {
                recordId: detail.recordId,
                referenceId: detail.referenceId,
                redirectUrl: detail.redirectUrl,
                summaryPageUrl: detail.summaryPageUrl,
                status: detail.status,
                errorMessage: detail.errorMessage,
                response: detail.response,
                data: detail.data,
              },
              null,
              2,
            )}
          </pre>
        </Modal>
      )}
    </div>
  )
}
